/**
 * MenuGate — route-level guard that mirrors the Menu Directory.
 *
 * When an admin flips a menu item to Hidden in Admin → Menu Directory,
 * the sidebar drops the link (via AppShell's `disabled` set). But a
 * founder who has the URL bookmarked, follows an old email link, or
 * hits an in-app breadcrumb pointing at that surface would still load
 * the page. This guard closes that gap: if the wrapping menu item is
 * hidden for the current user (plan-gated or admin-hidden), render a
 * <Navigate/> to the fallback (defaults to `/welcome`) instead.
 *
 * The gate reads from the app-wide `MenuVisibilityProvider` mounted at
 * the router root (see App.js) so it works both inside AND outside
 * `<AppShell>`. When there's no provider (e.g. Storybook), the default
 * no-op context makes the gate permissive — safer than hard-blocking.
 *
 * Usage:
 *   <MenuGate itemId="nav-ai-cpg-tools">
 *     <MyPage />
 *   </MenuGate>
 *
 *   // Or nested inside a layout:
 *   <Route element={<MenuGate itemId="nav-ai-center"><Outlet/></MenuGate>}>
 *     ...
 *   </Route>
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useMenuVisibility } from "../context/MenuVisibilityContext";

export default function MenuGate({ itemId, fallback = "/welcome", children }) {
  const { isDisabled, isPlanGated } = useMenuVisibility();
  // Plan-gated members: bounce them to Subscription & Billing instead
  // of the generic fallback so the CTA lands them next to the upgrade
  // button they need. Admin-hidden pages still route to `/welcome`
  // (or the caller-provided fallback) because that's a soft launch
  // toggle, not a paywall.
  if (isPlanGated(itemId)) return <Navigate to="/account/subscription" replace />;
  if (!isDisabled(itemId)) return children;
  // Guard against a redirect loop — if the fallback surface itself is
  // hidden, render children rather than bouncing between two hidden
  // routes. Admin will fix the Menu Directory; meanwhile a stale page
  // is preferable to a browser freeze.
  const fallbackItemId = FALLBACK_ITEM_MAP[fallback];
  if (fallbackItemId && isDisabled(fallbackItemId)) return children;
  return <Navigate to={fallback} replace />;
}

// Path → nav item mapping used only for the loop-guard above.
const FALLBACK_ITEM_MAP = {
  "/welcome": "nav-welcome",
  "/dashboard": "nav-dashboard",
};
